import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  DeleteButton,
  TextInput,
  NumberField,
  NumberInput,
  BooleanField,
  BooleanInput,
  required,
  regex,
  useTranslate,
  useRecordContext,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import EmptyList from '../components/misc/EmptyList';
import SourceLanguageBanner from '../components/form/SourceLanguageBanner';
import { useSourceLanguage } from '../hooks/useSourceLanguage';
import { EditableImage } from '../components/form/EditableImageField';
import { resolveMediaUrl } from '../utils/mediaUrl';
import ResourceListTabs from '../components/misc/ResourceListTabs';
import { TABBED_LIST_SX, TabbedListActionsPlaceholder } from '../components/misc/tabbedListLayout';
import { PageFormFields } from './loyaltyMobileAppsPage';

const entity = 'loyaltyMobileApps';

const IMAGE_SIZE = 160;
const IMAGE_ASPECT = 1;

const urlValidate = regex(/^(https?:\/\/)[^\s]+$/i, 'Geçerli bir URL girin (https://...)');

const optionalUrlValidate = (value) => {
  if (value == null || String(value).trim() === '') return undefined;
  return urlValidate(value, null);
};

const ColorFieldInput = ({ source, label, defaultValue = '#AE256D' }) => {
  const { setValue } = useFormContext();
  const value = useWatch({ name: source }) || defaultValue;
  const swatch = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : defaultValue;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 1, width: '100%' }}>
      <TextInput
        source={source}
        label={label}
        fullWidth
        defaultValue={defaultValue}
        helperText="Örn. #AE256D"
      />
      <Box
        component="input"
        type="color"
        value={swatch}
        onChange={(e) => {
          setValue(source, e.target.value, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }}
        sx={{
          width: 48,
          height: 40,
          border: '1px solid #ddd',
          borderRadius: 1,
          cursor: 'pointer',
          p: 0.25,
          mb: 2.5,
          flexShrink: 0,
        }}
      />
    </Box>
  );
};

const imagePreviewSrc = (image) => {
  if (!image) return null;
  if (typeof image === 'string') return resolveMediaUrl(image);
  if (image?.src) {
    if (typeof image.src === 'string' && image.src.startsWith('blob:')) return image.src;
    return resolveMediaUrl(image.src);
  }
  if (image?.rawFile instanceof Blob) {
    try {
      return URL.createObjectURL(image.rawFile);
    } catch {
      return null;
    }
  }
  return null;
};

const LoyaltyAppLivePreview = () => {
  const translate = useTranslate();
  const title = useWatch({ name: 'title' });
  const subtitle = useWatch({ name: 'subtitle' });
  const category = useWatch({ name: 'category' });
  const accentColor = useWatch({ name: 'accentColor' }) || '#AE256D';
  const image = useWatch({ name: 'image' });

  const titleText =
    (typeof title === 'object' ? title?.tr || title?.en : title) ||
    translate('loyaltyMobileApps.preview_title', { _: 'Başlık' });
  const subtitleText =
    (typeof subtitle === 'object' ? subtitle?.tr || subtitle?.en : subtitle) ||
    translate('loyaltyMobileApps.preview_subtitle', { _: 'Alt başlık' });
  const categoryText =
    (typeof category === 'object' ? category?.tr || category?.en : category) || '';
  const imgSrc = imagePreviewSrc(image);

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: '#F5F5F5',
        border: '1px solid #E8E8E8',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#555' }}>
        {translate('loyaltyMobileApps.preview', { _: 'Önizleme' })}
      </Typography>
      <Box
        sx={{
          width: 200,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1',
            background: imgSrc
              ? undefined
              : `linear-gradient(135deg, ${accentColor} 0%, #0E1F58 100%)`,
          }}
        >
          {imgSrc ? (
            <Box
              component="img"
              src={imgSrc}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : null}
          {categoryText ? (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 600,
                maxWidth: '80%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {categoryText}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', lineHeight: 1.25 }}>
            {titleText}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#757575', mt: 0.5, lineHeight: 1.3 }}>
            {subtitleText}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#AE256D',
              mt: 1,
              textAlign: 'right',
            }}
          >
            {translate('loyaltyMobileApps.preview_download', { _: 'İndir' })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const FormToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton alwaysEnable />
  </Toolbar>
);

const atLeastOneStoreUrl = (value, allValues) => {
  const ios = String(allValues?.iosStoreUrl || '').trim();
  const android = String(allValues?.androidStoreUrl || '').trim();
  if (ios || android) return undefined;
  return 'En az bir mağaza URL’si gerekli (iOS veya Android)';
};

const LoyaltyAppFormFields = ({ isEdit = false }) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
        {translate('loyaltyMobileApps.form_heading', { _: 'Sadakat Uygulaması' })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {translate('loyaltyMobileApps.form_subheading', {
          _: 'Mobil uygulamadaki sadakat kartları. Görsel, yazı, kategori ve mağaza linklerini buradan yönetin.',
        })}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
          width: '100%',
        }}
      >
        <Box>
          <TextInput
            source="name"
            label={translate('loyaltyMobileApps.name', { _: 'Dahili Ad' })}
            fullWidth
            validate={required()}
            helperText={translate('loyaltyMobileApps.name_helper', {
              _: 'Sadece panel listesinde görünür',
            })}
          />
          <NumberInput
            source="order"
            label={translate('loyaltyMobileApps.order', { _: 'Sıra' })}
            fullWidth
            defaultValue={0}
          />
          <BooleanInput
            source="isActive"
            label={translate('loyaltyMobileApps.is_active', { _: 'Aktif?' })}
            defaultValue={true}
          />

          <EditableImage
            source="image"
            record={record}
            aspect={IMAGE_ASPECT}
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
            outputMime="image/jpeg"
            label={translate('loyaltyMobileApps.image', { _: 'Kapak Görseli' })}
            hint={translate('loyaltyMobileApps.image_hint', {
              _: 'Kare (1:1) kapak önerilir. Yoksa accent rengi ile gradient gösterilir.',
            })}
            cropTitle={translate('loyaltyMobileApps.image_crop_title', {
              _: 'Kapak Kırp (1:1)',
            })}
            cropDesc={translate('loyaltyMobileApps.image_crop_desc', {
              _: 'Kırpma alanı kare oranında kilitli.',
            })}
          />

          <ColorFieldInput
            source="accentColor"
            label={translate('loyaltyMobileApps.accent_color', { _: 'Accent Rengi' })}
            defaultValue="#AE256D"
          />

          <TextInput
            source={localizedSource('title')}
            label={translate('loyaltyMobileApps.title_field', { _: 'Başlık' })}
            fullWidth
            validate={required()}
          />
          <TextInput
            source={localizedSource('subtitle')}
            label={translate('loyaltyMobileApps.subtitle', { _: 'Alt Başlık' })}
            fullWidth
          />
          <TextInput
            source={localizedSource('category')}
            label={translate('loyaltyMobileApps.category', { _: 'Kategori' })}
            fullWidth
            helperText={translate('loyaltyMobileApps.category_helper', {
              _: 'Kartın sol üst köşesindeki rozet',
            })}
          />
          <TextInput
            source="iosStoreUrl"
            label={translate('loyaltyMobileApps.ios_store_url', {
              _: 'App Store URL',
            })}
            fullWidth
            validate={[optionalUrlValidate, atLeastOneStoreUrl]}
            helperText={translate('loyaltyMobileApps.ios_store_helper', {
              _: 'iOS cihazlarda açılır',
            })}
          />
          <TextInput
            source="androidStoreUrl"
            label={translate('loyaltyMobileApps.android_store_url', {
              _: 'Play Store URL',
            })}
            fullWidth
            validate={[optionalUrlValidate, atLeastOneStoreUrl]}
            helperText={translate('loyaltyMobileApps.android_store_helper', {
              _: 'Android cihazlarda açılır',
            })}
          />
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
          <LoyaltyAppLivePreview />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const LoyaltyMobileAppList = (props) => {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'heading' ? 'heading' : 'apps';
  const tabs = [
    {
      id: 'apps',
      label: translate('loyaltyMobileApps.tab_apps', { _: 'Uygulamalar' }),
    },
    {
      id: 'heading',
      label: translate('loyaltyMobileApps.tab_heading', { _: 'Sayfa Başlığı' }),
    },
  ];

  if (activeTab === 'heading') {
    return (
      <Edit
        resource="loyaltyMobileAppsPage"
        id="default"
        title={translate('loyaltyMobileApps.title', { _: 'Sadakat Uygulamaları' })}
        mutationMode="pessimistic"
        actions={<TabbedListActionsPlaceholder />}
      >
        <ResourceListTabs activeTab="heading" tabs={tabs} />
        <SimpleForm redirect={false} toolbar={<FormToolbar />}>
          <PageFormFields isEdit />
        </SimpleForm>
      </Edit>
    );
  }

  return (
    <List
      {...props}
      title={translate('loyaltyMobileApps.title', { _: 'Sadakat Uygulamaları' })}
      empty={<EmptyList entity={entity} />}
      sort={{ field: 'order', order: 'ASC' }}
      exporter={false}
      sx={TABBED_LIST_SX}
    >
      <ResourceListTabs activeTab="apps" tabs={tabs} />
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label={translate('loyaltyMobileApps.name', { _: 'Ad' })} />
        <NumberField source="order" label={translate('loyaltyMobileApps.order', { _: 'Sıra' })} />
        <BooleanField
          source="isActive"
          label={translate('loyaltyMobileApps.is_active', { _: 'Aktif?' })}
        />
        <EditButton />
        <DeleteButton basePath={`/${entity}`} />
      </Datagrid>
    </List>
  );
};

export const LoyaltyMobileAppEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('loyaltyMobileApps.edit_title', { _: 'Sadakat Uygulaması Düzenle' })}
      {...props}
      mutationMode="pessimistic"
    >
      <SimpleForm redirect={`/${entity}`} toolbar={<FormToolbar />}>
        <LoyaltyAppFormFields isEdit />
      </SimpleForm>
    </Edit>
  );
};

export const LoyaltyMobileAppCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create
      title={translate('loyaltyMobileApps.create_title', { _: 'Yeni Sadakat Uygulaması' })}
      {...props}
    >
      <SimpleForm
        redirect={`/${entity}`}
        toolbar={<FormToolbar />}
        defaultValues={{
          isActive: true,
          order: 0,
          accentColor: '#AE256D',
        }}
      >
        <LoyaltyAppFormFields />
      </SimpleForm>
    </Create>
  );
};
